import Image from 'next/image'

/**
 * The identity header for each of the dashboard's four doors.
 *
 * Its whole job is continuity: the hue and the artwork a student meets on the
 * dashboard tile are the ones that greet them on arrival, so the colour system
 * teaches itself. Indigo is always Tests, emerald always Practice, amber
 * always Study, cyan always Progress — the same mapping as the --sec-* /
 * --attn / --analytics tokens.
 *
 * Deliberately presentational: no data, no state. A page that needs a stat in
 * its header renders it underneath rather than growing this component, which
 * is how the four headers stay identical in shape.
 */
export type SectionKind = 'practice' | 'tests' | 'study' | 'progress'

const SKIN: Record<
  SectionKind,
  { icon: string; band: string; title: string; sub: string; rule: string }
> = {
  practice: {
    icon: '/images/dashboard/practice.png',
    band: 'from-emerald-50 to-card dark:from-emerald-950/40',
    title: 'text-emerald-950 dark:text-emerald-100',
    sub: 'text-emerald-800/80 dark:text-emerald-300/80',
    rule: 'bg-emerald-500',
  },
  tests: {
    icon: '/images/dashboard/tests.png',
    band: 'from-indigo-50 to-card dark:from-indigo-950/40',
    title: 'text-indigo-950 dark:text-indigo-100',
    sub: 'text-indigo-800/80 dark:text-indigo-300/80',
    rule: 'bg-indigo-500',
  },
  study: {
    icon: '/images/dashboard/study.png',
    band: 'from-amber-50 to-card dark:from-amber-950/40',
    title: 'text-amber-950 dark:text-amber-100',
    sub: 'text-amber-900/80 dark:text-amber-300/80',
    rule: 'bg-amber-500',
  },
  progress: {
    icon: '/images/dashboard/progress.png',
    band: 'from-cyan-50 to-card dark:from-cyan-950/40',
    title: 'text-cyan-950 dark:text-cyan-100',
    sub: 'text-cyan-800/80 dark:text-cyan-300/80',
    rule: 'bg-cyan-500',
  },
}

export default function SectionHero({
  kind,
  title,
  subtitle,
  children,
}: {
  kind: SectionKind
  title: string
  subtitle?: string
  /** Optional trailing slot — a CTA or a single stat, never a second header. */
  children?: React.ReactNode
}) {
  const skin = SKIN[kind]
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br ${skin.band} p-5`}
    >
      <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-1 ${skin.rule}`} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3.5">
          <Image
            src={skin.icon}
            alt=""
            width={46}
            height={46}
            className="shrink-0 rounded-xl"
            priority
          />
          <div className="min-w-0">
            <h1 className={`text-xl font-semibold tracking-tight ${skin.title}`}>{title}</h1>
            {subtitle && <p className={`mt-0.5 text-[13px] ${skin.sub}`}>{subtitle}</p>}
          </div>
        </div>
        {children}
      </div>
    </div>
  )
}
